import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Hero from "@/components/Hero";
import TrustBar from "@/components/TrustBar";
import SectionHeading from "@/components/SectionHeading";
import ServiceCard from "@/components/ServiceCard";
import Reveal from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import Testimonials from "@/components/Testimonials";
import FramedPhoto from "@/components/FramedPhoto";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import { SERVICES, COMMERCIAL_BENEFITS, WHY_CHOOSE, PROCESS, PHONE_DISPLAY } from "@/data/site";
import { PROJECTS } from "@/data/gallery";

const MARQUEE = [
  "Residential Cleaning",
  "Commercial Cleaning",
  "Move-In / Move-Out",
  "Junk Removal",
  "Property Cleanouts",
  "Apartment Turnovers",
  "Rental Properties",
];

export default function Home() {
  return (
    <>
      <Seo
        title="Cleaning & Junk Removal Greensboro NC | Authentically Yours"
        description="Authentically Yours Property Services offers residential and commercial cleaning, move-in/move-out cleaning and junk removal in Greensboro, NC. Free estimates."
        path="/"
      />

      <Hero />
      <TrustBar />

      {/* Brief intro */}
      <section className="container-x pt-20 sm:pt-28 pb-2" aria-label="Introduction">
        <Reveal className="max-w-3xl">
          <span className="eyebrow text-rose">Authentically Yours Property Services</span>
          <p className="mt-4 font-serif text-2xl sm:text-[2rem] leading-snug text-ink">
            Locally owned and hands-on, we provide dependable residential and
            commercial property services across Greensboro and the surrounding
            Triad — cleaning, move-in/move-out, and junk removal, done with care.
          </p>
        </Reveal>
      </section>

      {/* Services */}
      <section className="container-x py-20 sm:py-28" aria-labelledby="services-h">
        <SectionHeading
          eyebrow="What We Do"
          title="Property Care Without the Hassle"
          subtitle="Reliable services designed to keep homes, rentals, businesses, and properties looking their best."
          as="h2"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <ServiceCard {...s} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Editorial marquee */}
      <div className="border-y border-gold/20 bg-white py-6 overflow-hidden" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {MARQUEE.map((m) => (
                  <span key={m + dup} className="flex items-center">
                    <span className="font-serif italic text-2xl sm:text-3xl text-ink/80 px-8">{m}</span>
                    <span className="h-1.5 w-1.5 rounded-full bg-rose" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Commercial / Property management */}
      <section className="bg-ink text-white" aria-labelledby="commercial-h">
        <div className="container-x py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              eyebrow="For Property Professionals"
              title="Property Services That Work Around Your Business"
              subtitle="From apartment turnovers and rental properties to offices and commercial spaces, Authentically Yours Property Services helps property professionals keep spaces clean, presentable, and ready for what's next."
              as="h2"
              dark
            />
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {COMMERCIAL_BENEFITS.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 text-white/85">
                  <Icon size={18} strokeWidth={1.25} className="text-blush shrink-0" aria-hidden="true" />
                  <span className="text-[0.95rem]">{label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/commercial" className="btn-primary btn-on-dark" data-testid="commercial-explore">
                Explore Commercial Services
              </Link>
              <Link to="/free-estimate" className="btn-ghost btn-on-dark" data-testid="commercial-estimate">
                Request a Commercial Estimate
              </Link>
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -inset-3 border border-gold/25" aria-hidden="true" />
              <FramedPhoto
                src="/assets/cleaning/commercial.webp"
                alt="Bright, modern commercial office lobby with polished floors and lounge seating"
                aspect="4 / 3"
                className="relative"
                testId="commercial-graphic"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Before & After preview */}
      <section className="container-x py-20 sm:py-28" aria-labelledby="work-h">
        <SectionHeading
          eyebrow="Our Work"
          title="The Difference Is in the Details"
          subtitle="A quick look at real transformations across the Greensboro area."
          as="h2"
        />
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {PROJECTS.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <BeforeAfterSlider project={p} />
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/our-work" className="btn-ghost" data-testid="home-view-work">
            View Our Work <ArrowRight size={16} strokeWidth={1.75} aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* Why choose us — bento */}
      <section className="bg-blush-soft/50 border-y border-gold/15" aria-labelledby="why-h">
        <div className="container-x py-20 sm:py-28">
          <SectionHeading
            eyebrow="Why Authentically Yours"
            title="Service You Can Feel Good About"
            as="h2"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="h-full card-lift bg-white border border-gold/20 p-8">
                  <span className="inline-flex h-12 w-12 items-center justify-center border border-rose/30 text-rose mb-6" aria-hidden="true">
                    <Icon size={22} strokeWidth={1.25} />
                  </span>
                  <h3 className="text-2xl text-ink">{title}</h3>
                  <p className="mt-3 text-ink/70 text-[0.95rem] leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About owners preview */}
      <section className="container-x py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center" aria-labelledby="about-h">
        <Reveal>
          <div className="relative max-w-md">
            <div className="absolute -inset-3 border border-gold/25" aria-hidden="true" />
            <FramedPhoto
              src="/assets/cleaning/counter.webp"
              alt="Cleaner wiping down a kitchen counter to a spotless finish"
              aspect="4 / 5"
              className="relative"
              testId="owner-graphic"
            />
          </div>
        </Reveal>
        <div>
          <SectionHeading
            eyebrow="About Us"
            title="Meet the People Behind Authentically Yours"
            as="h2"
          />
          <div className="mt-6 space-y-5 text-ink/75 leading-relaxed max-w-xl">
            <p>
              Authentically Yours Property Services LLC was created with a simple
              purpose: to provide dependable property services with the
              professionalism, care, and personal attention customers deserve.
            </p>
            <p>
              As a locally owned company, our owners are hands-on in creating an
              experience built around reliability, communication, and treating
              every property with respect. Whether we're helping a homeowner
              refresh their space or supporting a property professional with a
              turnover or cleanout, we want every client to feel confident about
              who they're working with.
            </p>
          </div>
          <p className="mt-6 font-serif text-2xl text-ink">Kiarah Ayers &amp; Daryl Thomas</p>
          <p className="eyebrow text-rose mt-1">Co-Owners, Authentically Yours Property Services LLC</p>
          <div className="mt-8">
            <Link to="/about" className="btn-ghost" data-testid="home-our-story">
              Our Story <ArrowRight size={16} strokeWidth={1.75} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-ink text-white" aria-labelledby="process-h">
        <div className="container-x py-20 sm:py-28">
          <SectionHeading eyebrow="How It Works" title="Simple From Start to Finish" as="h2" dark />
          <ol className="mt-14 grid gap-px sm:grid-cols-2 lg:grid-cols-4 bg-gold/15 border border-gold/15">
            {PROCESS.map(({ n, title, body }, i) => (
              <Reveal as="li" key={n} delay={i * 0.08} className="bg-ink p-8">
                <span className="font-serif text-5xl text-blush">{n}</span>
                <span className="mt-2 block h-px w-10 bg-gold/50" aria-hidden="true" />
                <h3 className="mt-5 text-2xl text-white">{title}</h3>
                <p className="mt-3 text-white/65 text-[0.95rem] leading-relaxed">{body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Service area */}
      <section className="container-x py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center" aria-labelledby="area-h">
        <div>
          <SectionHeading
            eyebrow="Service Area"
            title="Proudly Serving Greensboro & Surrounding Areas"
            as="h2"
          />
          <p className="mt-6 text-ink/75 leading-relaxed max-w-xl">
            Based in Greensboro, North Carolina, Authentically Yours Property
            Services proudly serves Greensboro and the many surrounding cities and
            communities across the Triad — including nearby towns throughout
            Guilford County and beyond.
          </p>
          <p className="mt-5 text-ink/75 leading-relaxed max-w-xl">
            Not sure if you're in our service area? Call or text us at{" "}
            <span className="text-ink font-medium">{PHONE_DISPLAY}</span>.
          </p>
          <div className="mt-8">
            <Link to="/service-areas" className="btn-ghost" data-testid="home-service-areas">
              View Service Areas <ArrowRight size={16} strokeWidth={1.75} aria-hidden="true" />
            </Link>
          </div>
        </div>
        <Reveal delay={0.1}>
          <ServiceAreaMap className="max-w-lg mx-auto" />
        </Reveal>
      </section>

      <Testimonials />

      <CtaSection />
    </>
  );
}
